/**
 * Paste Action
 * Moves cut items to a new location
 */

import { Datum } from "../datastructure";
import { ActionDefinition, ActionContext } from "./types";
import { getCutItems, clearCutItems } from "./cut";
import { moveItems } from "./moveItems";
import { isReadOnlyDatum } from "../specialFolders";

/**
 * Paste cut items into a folder
 */
export async function pasteItems(
  targetFolder: Datum,
  context: ActionContext
): Promise<void> {
  if (targetFolder.type !== "folder") {
    context.showToast?.("Can only paste into folders", "error");
    return;
  }

  const cutItems = getCutItems();
  if (cutItems.length === 0) {
    context.showToast?.("Nothing to paste", "info");
    return;
  }

  if (!context.filetree) {
    console.error("paste: Filetree not available in context");
    context.showToast?.("Unable to paste - please try again", "error");
    return;
  }

  // Convert cut items back to Datum objects
  const itemDatums: Datum[] = [];
  for (const item of cutItems) {
    const datum = context.filetree.lookup.get(item.datumId);
    if (datum) {
      itemDatums.push(datum);
    }
  }

  if (itemDatums.length === 0) {
    context.showToast?.("Cut items no longer exist", "error");
    clearCutItems();
    return;
  }

  // Use shared move logic
  const success = await moveItems({
    items: itemDatums,
    target: targetFolder,
    filetree: context.filetree,
    context,
  });

  if (success) {
    clearCutItems();
  }
}

/**
 * Action definition for context menu
 */
export const pasteAction: ActionDefinition = {
  config: {
    action: "paste",
    label: "Paste",
  },
  isApplicable: (datum, context) => {
    // Only show paste on folders when there are items to paste
    if (datum.type !== "folder") return false;
    // Cut items are recorded as datum ids resolved through the live tree, so a
    // surface without one (search results) cannot complete a paste.
    if (!context?.filetree) return false;
    if (isReadOnlyDatum(datum, context)) return false;
    const cutItems = getCutItems();
    return cutItems.length > 0;
  },
  execute: pasteItems,
};
